import { ContentCard } from '@braze/react-native-sdk';
import { IMarketingCardRules } from 'features/content/components/marketing-card-group/types';
import {
  hasRules,
  tileShouldBeDisplayed,
} from 'features/content/components/marketing-card-group/utils';
import {
  parseDayParts,
  parseDaysOfWeek,
  parseTimeRanges,
} from 'features/notifications/components/braze-marketing-card/utils';

import { IDayPartBoundary } from 'state/menu/main-menu/types';
import { ServiceMode } from 'state/service-mode/types';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';

import {
  CONTENT_CARD_FEED,
  CONTENT_CARD_SETTING,
  ContentCardTypes,
  WebContentCard,
} from '../types';

export const MAX_CONTENT_CARDS_DISPLAYED = 6;

export const parseWebContentCards = (webcards: WebContentCard[]): ContentCard[] => {
  return webcards
    .map(webcard => {
      const sharedProps = {
        id: webcard.id,
        url: webcard.url || '',
        image: webcard.imageUrl || '',
        created: new Date(webcard.created).getTime(),
        expiresAt: new Date(webcard.expiresAt).getTime(),
        pinned: webcard.pinned,
        dismissible: webcard.dismissible,
        dismissed: webcard.dismissed,
        clicked: webcard.clicked,
        viewed: webcard.viewed,
        isControl: webcard.isControl,
        extras: webcard.extras,
        openURLInWebView: false,
      };

      const cardTypeMap: Record<string, Partial<ContentCard>> = {
        'ab-captioned-image': {
          type: ContentCardTypes.CAPTIONED,
          title: webcard.title || '',
          cardDescription: webcard.description || '',
          domain: webcard.linkText || '',
          imageAspectRatio: webcard.aspectRatio,
        },
        'ab-banner ab-image-only': {
          type: ContentCardTypes.IMAGE_ONLY,
          imageAspectRatio: webcard.aspectRatio,
        },
        'ab-classic-card': {
          type: ContentCardTypes.CLASSIC,
          title: webcard.title || '',
          cardDescription: webcard.description || '',
          domain: webcard.linkText || '',
        },
      };

      const cardClassType = webcard.ic;
      if (!cardTypeMap[cardClassType] && !webcard.isControl) {
        return null;
      }

      return {
        ...sharedProps,
        ...cardTypeMap[cardClassType],
      } as ContentCard;
    })
    .filter((card): card is ContentCard => card !== null);
};

/**
 * Id correlating a Content Card with a Notification Center record, or `null` if the card has no
 * `notification-id` extra - such cards are never tracked, never falling back to the Braze card id.
 */
export const getContentCardNotificationId = (card: ContentCard): string | null => {
  const notificationId = card.extras?.[CONTENT_CARD_SETTING.notificationId]?.trim();
  return notificationId || null;
};

// Shared by the read-status query (what it requests) and the derived read state (what it must
// see covered by a resolved query) so the two can't drift apart. Control cards are excluded: they
// are never shown, so they are never classified or marked read, and the unread count skips them.
export const getEligibleNotificationIds = (cards: ContentCard[]): string[] =>
  cards
    .filter(({ isControl }) => !isControl)
    .map(getContentCardNotificationId)
    .filter((id): id is string => id !== null);

// No pruning by whether Braze still returns the card: a campaign that briefly stops being
// returned (targeting change) and is later resent under the same notification-id must not lose
// its read memory in the gap.
const READ_CONTENT_CARD_TTL_MS = 365 * 24 * 60 * 60 * 1000;

type ReadContentCardsStore = Record<string, number>;

const getReadContentCardsStore = (): ReadContentCardsStore =>
  syncStorage.getObject<ReadContentCardsStore>(SyncStorageKey.READ_CONTENT_CARDS) || {};

/**
 * Ids of Content Cards read on this device, by any identity - not scoped to the current one, so a
 * card read while authenticated still reads as read after logout (server-backed state is cleared
 * on session end, this isn't). Expired entries are excluded even before the next write prunes them.
 */
export const getReadContentCardsFromLocalStorage = (): string[] => {
  const now = Date.now();
  const store = getReadContentCardsStore();
  return Object.keys(store).filter(id => store[id] > now);
};

/**
 * Records ids as read on this device, refreshing their expiration. Called from the anonymous
 * path, and - once server-confirmed - the authenticated path too, deliberately sharing one store
 * so a card read while authenticated survives logout. Also drops expired entries on every write.
 */
export const setReadContentCardsInLocalStorage = (ids: string[]) => {
  const now = Date.now();
  const store = getReadContentCardsStore();
  const nextStore: ReadContentCardsStore = {};

  for (const id of Object.keys(store)) {
    if (store[id] > now) {
      nextStore[id] = store[id];
    }
  }

  const expiresAt = now + READ_CONTENT_CARD_TTL_MS;
  for (const id of ids) {
    nextStore[id] = expiresAt;
  }

  syncStorage.setObject(SyncStorageKey.READ_CONTENT_CARDS, nextStore);
};

// Cards without the feed property will be visible in both feeds. For the News feed specifically,
// we need to add this property to calculate unread messages and display the badge on the bell icon.
export const filterContentCardsByFeed = <T extends ContentCard | WebContentCard>({
  cards,
  isNewsFeedEnabled = false,
  isHomePageFeedEnabled = false,
}: {
  cards: T[];
  isNewsFeedEnabled: boolean;
  isHomePageFeedEnabled: boolean;
}): T[] => {
  let currentFeed = '';

  if (isNewsFeedEnabled) {
    currentFeed = CONTENT_CARD_FEED.NEWS;
  } else if (isHomePageFeedEnabled) {
    currentFeed = CONTENT_CARD_FEED.HOME_PAGE;
  }

  return cards
    .filter(({ extras }) => !extras.feed || extras.feed === currentFeed)
    .map(card => ({
      ...card,
      extras: {
        ...card.extras,
        ...(!card.extras.feed && currentFeed && { feed: currentFeed }),
      },
    }));
};

export const dedupeByTitleAndDescription = (contentCards: ContentCard[]): ContentCard[] => {
  const latestByKey = new Map<string, ContentCard>();
  const passthrough: ContentCard[] = [];

  for (const card of contentCards) {
    if ('title' in card && 'cardDescription' in card) {
      const title = card.title ?? '';
      const description = card.cardDescription ?? '';

      if (!title || !description) {
        passthrough.push(card);
        continue;
      }

      const key = `${title}|${description}`;
      const existing = latestByKey.get(key);

      if (!existing || card.created > existing.created) {
        latestByKey.set(key, card);
      }
    } else {
      passthrough.push(card);
    }
  }

  return [...latestByKey.values(), ...passthrough].sort((a, b) => b.created - a.created);
};

export const filterContentCardsByServiceMode = <T extends ContentCard | WebContentCard>(
  contentCards: T[],
  serviceMode: ServiceMode | null
): T[] => {
  if (!serviceMode) {
    return contentCards;
  }

  const selectedServiceMode = serviceMode === ServiceMode.DELIVERY ? 'delivery' : 'pickup';

  return contentCards.filter(({ extras }) => {
    const cardServiceMode = extras[CONTENT_CARD_SETTING.serviceMode]?.trim().toLowerCase();
    if (!cardServiceMode) {
      return true;
    }

    if (cardServiceMode !== 'delivery' && cardServiceMode !== 'pickup') {
      return true;
    }

    return cardServiceMode === selectedServiceMode;
  });
};

// Counts eligible unread News feed cards in the same capped visible set rendered by the inbox. The
// cards beyond the cap remain in the feed and unread state, but do not keep the badge visible while
// the inbox has no way to display them. Braze's `viewed` flag is never consulted.
export const countUnreadNewsFeedCards = (
  newsFeedContentCards: ContentCard[],
  readNotificationIds: string[],
  serviceMode: ServiceMode | null
): number => {
  return filterContentCardsByServiceMode(newsFeedContentCards, serviceMode)
    .filter(({ isControl }) => !isControl)
    .slice(0, MAX_CONTENT_CARDS_DISPLAYED)
    .filter(card => {
      const notificationId = getContentCardNotificationId(card);

      return (
        notificationId !== null &&
        card.extras.feed === CONTENT_CARD_FEED.NEWS &&
        !readNotificationIds.includes(notificationId)
      );
    }).length;
};

// Filter out Content Cards that are not within the allowed days of the week or time ranges.
export const filterContentCardsByRules = <T extends ContentCard | WebContentCard>(
  contentCards: T[],
  activeDayParts: IDayPartBoundary[]
): T[] => {
  const hasActiveDayParts = activeDayParts.length > 0;

  return contentCards.filter(({ extras }) => {
    const rules: IMarketingCardRules = {
      daysOfWeek: parseDaysOfWeek(extras[CONTENT_CARD_SETTING.daysOfWeek]),
      timeRanges: parseTimeRanges(extras[CONTENT_CARD_SETTING.timeRanges]),
    };
    const showCardByMarketingCardRules = !hasRules(rules) || tileShouldBeDisplayed(rules);

    const cardDayParts = parseDayParts(extras[CONTENT_CARD_SETTING.dayParts]);
    const showByDayPart =
      hasActiveDayParts && cardDayParts && cardDayParts.length > 0
        ? cardDayParts?.some(cardDayPart =>
            activeDayParts.some(({ key }) => key.toLowerCase() === cardDayPart)
          )
        : true;

    const excludedCardDayParts = parseDayParts(extras[CONTENT_CARD_SETTING.excludedDayParts]);

    const isExcludedDayPart =
      hasActiveDayParts &&
      excludedCardDayParts?.some(excludedDayPart =>
        activeDayParts.some(({ key }) => key.toLowerCase() === excludedDayPart)
      );
    return showCardByMarketingCardRules && showByDayPart && !isExcludedDayPart;
  });
};
